import React, { useEffect, useRef, useState, useCallback } from 'react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { useDebounce } from 'common-react';
import Backdrop from '@material-ui/core/Backdrop';
import Popper, { PopperProps } from '@material-ui/core/Popper';
import Paper, { PaperProps } from '@material-ui/core/Paper';
import Autocomplete, { AutocompleteCloseReason, AutocompleteRenderOptionState, AutocompleteRenderGroupParams, AutocompleteRenderInputParams, AutocompleteInputChangeReason } from '@material-ui/lab/Autocomplete';
import {
  AutocompleteModal,
  AutocompleteButton,
  AutocompleteRenderInput,
  AutocompleteRenderGroup,
  AutocompleteRenderOption
} from './components';
import {
  AutocompleteModel,
  AutocompleteOptionModel,
  useAutocomplete,
  useDropdownPosition,
  AutocompleteMobileType
} from './tools';
import { ReactComponent as SearchIcon } from 'common-assets/assets/icons/magnifyingGlass.svg';
import { FieldLabel } from '../FieldLabel/FieldLabel';

import useStyles from './Autocomplete.style';

const CustomAutocomplete: React.FC<AutocompleteModel> = ({
  inputValue,
  buttonPlaceholder,
  searchLabel,
  searchPlaceholder,
  modalTitle,
  options,
  getOptionLabel,
  renderOption,
  onInputChange,
  onChange,
  groupBy,
  renderGroup,
  renderIcon,
  modalFooter,
  value,
  getOptionSelected,
  mobileType = AutocompleteMobileType.FULL,
  disabled,
  open,
  className,
  mobileButtonClassName,
  optionClassName,
  groupListClassName,
  required = false,
  ariaDescribedby,
  label,
  autoTestId,
}) => {
  const [t] = useTranslation('components');
  const c = useStyles({});
  const [internalInputValue, setInternalInputValue] = useState(inputValue || '');
  const debouncedInputValue = useDebounce(internalInputValue, 150);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const maxDropdownHeight = 440;
  const {
    option,
    isModalOpen,
    setIsModalOpen,
    hasUserChangedOption,
    autocompleteRef,
    handleChange,
    handleInputChange: originalHandleInputChange,
    isMobile
  } = useAutocomplete({ value, onChange, onInputChange, groupBy, getOptionLabel });

  const positionAbove = useDropdownPosition({
    isModalOpen,
    isMobile,
    buttonRef,
    maxDropdownHeight,
  });
  const prevIsMobileRef = useRef(isMobile);

  useEffect(() => {
    if (inputValue !== undefined) {
      setInternalInputValue(inputValue);
    }
  }, [inputValue]);

  useEffect(() => {
    if (isModalOpen) {
      setInternalInputValue('');
    }
  }, [isModalOpen]);

  useEffect(() => {
    if (prevIsMobileRef.current !== isMobile) {
      setIsModalOpen(false);
      prevIsMobileRef.current = isMobile;
    }
  }, [isMobile, setIsModalOpen]);

  useEffect(() => {
    if (!isModalOpen || isMobile) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        setIsModalOpen(false);
        buttonRef.current?.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isModalOpen, isMobile, setIsModalOpen]);

  const handleInputChange = useCallback((event: React.ChangeEvent<{}>, newValue: string, reason: AutocompleteInputChangeReason) => {
    setInternalInputValue(newValue);
    originalHandleInputChange(event, newValue, reason);
  }, [originalHandleInputChange]);

  const handleClearInput = useCallback((event: React.MouseEvent) => {
    event.stopPropagation();
    setInternalInputValue('');
    handleInputChange(event, '', 'clear');
  }, [handleInputChange]);

  const CustomPopper = useCallback((props: PopperProps) => {
    const popperClassName = clsx(props.className, { above: positionAbove });
    return (
      <Popper
        {...props}
        placement={positionAbove ? "top-start" : "bottom-start"}
        className={popperClassName}
        modifiers={{
          flip: {
            enabled: false,
          },
          preventOverflow: {
            enabled: true,
            boundariesElement: 'viewport',
          },
        }}
      />
    );
  }, [positionAbove]);

  const CustomPaper = (props: PaperProps) => <Paper {...props} elevation={1} className={c.paper} />;

  const autocompleteConfig = {
    open: isModalOpen && !isMobile,
    inputValue: internalInputValue,
    disabled,
    options,
    groupBy,
    getOptionLabel,
    getOptionSelected,
    openOnFocus: true,
    value: option,
    noOptionsText: t('autocomplete.no-results'),
    className: clsx(c.root, { [c.rootAbove]: positionAbove }, className),
    classes: {
      paper: clsx(c.optionsWrapper),
      option: clsx(c.option, optionClassName ?? ''),
      input: c.input,
      noOptions: c.noOptions,
      listbox: c.listbox,
      popper: positionAbove ? 'above' : '',
      popupIndicator: c.hiddenPopupIndicator
    },
    disablePortal: true,
    disableClearable: true,
    fullWidth: true,
    clearOnBlur: !isMobile,
    onChange: handleChange,
    onInputChange: handleInputChange,
    PopperComponent: CustomPopper,
    PaperComponent: CustomPaper,
    renderGroup: (params: AutocompleteRenderGroupParams) => (
      <AutocompleteRenderGroup
        params={params}
        groupLabelClassName={c.groupLabel}
        groupListClassName={clsx(c.groupList, groupListClassName ?? '')}
        renderGroup={renderGroup}
      />
    ),
    renderOption: (opt: AutocompleteOptionModel, state: AutocompleteRenderOptionState) => (
      <AutocompleteRenderOption
        option={opt}
        state={state}
        hasUserChangedOption={hasUserChangedOption.current}
        debouncedInputValue={debouncedInputValue}
        getOptionLabel={getOptionLabel}
        filterTextClassName={c.filterText}
        renderOption={renderOption}
      />
    ),
    renderInput: (params: AutocompleteRenderInputParams) => (
      <AutocompleteRenderInput
        params={params}
        isMobile={isMobile}
        searchLabel={searchLabel}
        searchPlaceholder={searchPlaceholder}
        required={required}
        ariaDescribedby={ariaDescribedby}
        autocompleteRef={autocompleteRef}
        internalInputValue={internalInputValue}
        handleClearInput={handleClearInput}
      />
    ),
  };

  const renderMobileAutocomplete = () => (
    <AutocompleteModal
      title={modalTitle}
      isOpen={isModalOpen}
      handleClose={() => setIsModalOpen(false)}
      modalFooter={modalFooter}
    >
      <div className={c.modalInner}>
        <Autocomplete<AutocompleteOptionModel, false, boolean, false>
          {...autocompleteConfig}
          open={true}
          disablePortal={true}
          PopperComponent={undefined}
          PaperComponent={undefined}
          popupIcon={<SearchIcon aria-hidden={true} />}
        />
      </div>
    </AutocompleteModal>
  );

  const renderDesktopAutocomplete = () => {
    if (!isModalOpen) return null;

    return (
      <Autocomplete<AutocompleteOptionModel, false, boolean, false>
        {...autocompleteConfig}
        popupIcon={<SearchIcon aria-hidden={true} />}
      />
    );
  };

  return (
    <div className={c.autocompleteWrapper}>
      {label && <FieldLabel htmlFor="">{label}</FieldLabel>}
      <AutocompleteButton
        ref={buttonRef}
        mobileType={mobileType}
        buttonPlaceholder={buttonPlaceholder}
        option={option}
        getOptionLabel={getOptionLabel}
        renderIcon={renderIcon}
        onClick={() => setIsModalOpen(true)}
        className={mobileButtonClassName}
        isOpen={isModalOpen}
        ariaDescribedby={ariaDescribedby}
        disabled={disabled}
        autoTestId={autoTestId}
      />
      <Backdrop
        open={isModalOpen && !isMobile}
        invisible={true}
        className={c.desktopBackdrop}
        onClick={() => setIsModalOpen(false)}
      />
      {isMobile ? renderMobileAutocomplete() : renderDesktopAutocomplete()}
    </div>
  );
};

export { CustomAutocomplete as Autocomplete };
